import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeCodeForTokens, getAndClearState, type OAuthConfig, type OAuthTokens } from '../../lib/oauth';

// Get OAuth config - matches AuthContext
const getOAuthConfig = (): OAuthConfig => {
  const currentHost = window.location.hostname;
  const tesUrl = import.meta.env.VITE_TES_API_URL || `http://${currentHost}:8788`;
  const redirectUri = `${window.location.origin}/auth/callback`;

  return {
    authorizeUrl: `${tesUrl}/oauth/authorize`,
    // Use local proxy to avoid CORS issues with direct TES token endpoint
    tokenUrl: `${window.location.origin}/api/auth/token`,
    clientId: import.meta.env.VITE_OAUTH_CLIENT_ID || 'lego-test',
    redirectUri,
    scope: 'mcp:read mcp:write',
  };
};

export default function AuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check for error response
      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      // Validate required parameters
      if (!code) {
        setError('No authorization code received');
        return;
      }

      // Validate state
      const storedState = getAndClearState();
      if (storedState && state !== storedState) {
        setError('Invalid state parameter');
        return;
      }

      try {
        const config = getOAuthConfig();
        const tokens = await exchangeCodeForTokens(config, code);

        // Notify auth context of new tokens
        const authUpdate = (window as unknown as { __authTokenUpdate?: (tokens: OAuthTokens) => void }).__authTokenUpdate;
        if (authUpdate) {
          authUpdate(tokens);
        }

        // Redirect to main dashboard
        navigate('/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full mx-4">
          <div className="card p-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="btn btn-primary"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="flex space-x-2 justify-center mb-4">
          <div className="w-3 h-3 bg-primary-500 rounded-full loading-dot" />
          <div className="w-3 h-3 bg-primary-500 rounded-full loading-dot" />
          <div className="w-3 h-3 bg-primary-500 rounded-full loading-dot" />
        </div>
        <p className="text-gray-600 dark:text-gray-400">Completing authentication...</p>
      </div>
    </div>
  );
}
